import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useApp } from '../../context/AppContext';
import { getApiErrorMessage } from '../../lib/apiError';
import {
  AlertTriangle,
  Calendar,
  CheckCircle2,
  Clock,
  MapPin,
  RefreshCw,
  Users
} from 'lucide-react';

const watDate = (value, options = {}) => {
  if (!value) return '-';
  return new Intl.DateTimeFormat('en-NG', {
    timeZone: 'Africa/Lagos',
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    ...options
  }).format(new Date(value));
};

const statusStyles = {
  Proposed: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  Approved: 'bg-green-100 text-green-800 border-green-200',
  Unscheduled: 'bg-red-100 text-red-800 border-red-200',
  Rejected: 'bg-gray-100 text-gray-700 border-gray-200',
};

const StatCard = ({ label, value, tone = 'burgundy' }) => {
  const tones = {
    burgundy: 'border-burgundy-100 text-burgundy-700',
    green: 'border-green-100 text-green-700',
    yellow: 'border-yellow-100 text-yellow-700',
    red: 'border-red-100 text-red-700',
  };
  return (
    <div className={`rounded-2xl border bg-white p-5 ${tones[tone]}`}>
      <p className="font-serif text-3xl font-semibold">{value}</p>
      <p className="mt-1 text-sm uppercase tracking-wide text-gray-500">{label}</p>
    </div>
  );
};

const SchedulingPage = () => {
  const { authApi } = useAuth();
  const { fetchData } = useApp();
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [actionId, setActionId] = useState('');
  const [proposals, setProposals] = useState([]);
  const [visits, setVisits] = useState([]);
  const [summary, setSummary] = useState(null);
  const [lastRun, setLastRun] = useState(null);
  const [jobStatus, setJobStatus] = useState('');
  const [error, setError] = useState('');

  const loadScheduling = async () => {
    setError('');
    setLoading(true);
    try {
      const [proposalRes, visitRes] = await Promise.all([
        authApi.get('/schedule-proposals'),
        authApi.get('/supervisor/visits')
      ]);
      setProposals(proposalRes.data?.proposals || []);
      setVisits(visitRes.data?.visits || []);
      setSummary({
        weekStart: proposalRes.data?.weekStart || visitRes.data?.weekStart,
        weekEnd: proposalRes.data?.weekEnd || visitRes.data?.weekEnd,
        timezone: 'WAT'
      });
    } catch (err) {
      setError(getApiErrorMessage(err, 'Unable to load scheduling data.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadScheduling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const grouped = useMemo(() => {
    const proposed = proposals.filter(item => item.status === 'Proposed');
    const approved = proposals.filter(item => item.status === 'Approved');
    const unscheduled = proposals.filter(item => item.status === 'Unscheduled');
    return { proposed, approved, unscheduled };
  }, [proposals]);

  const runGenerator = async () => {
    setRunning(true);
    setError('');
    setJobStatus('Queued');
    try {
      const response = await authApi.post('/schedule-proposals/generate');
      setSummary(response.data?.targetWeek || response.data);
      const jobId = response.data?.jobId;
      if (!jobId) {
        throw new Error('Scheduler did not return a job id.');
      }
      let completedJob = null;
      for (let attempt = 0; attempt < 90; attempt += 1) {
        const jobResponse = await authApi.get(`/jobs/${jobId}`);
        const job = jobResponse.data;
        setJobStatus(job.status || 'queued');
        if (job.status === 'completed') {
          completedJob = job;
          break;
        }
        if (job.status === 'failed') {
          throw new Error(job.lastError || 'Schedule generation failed.');
        }
        await new Promise(resolve => setTimeout(resolve, 1500));
      }
      if (!completedJob) {
        throw new Error('Schedule generation is still running. Please refresh in a moment.');
      }
      setLastRun(completedJob.result || completedJob);
      await loadScheduling();
    } catch (err) {
      setError(getApiErrorMessage(err, err.message || 'Unable to generate schedule proposals.'));
    } finally {
      setJobStatus('');
      setRunning(false);
    }
  };

  const approveProposal = async (proposalId) => {
    setActionId(proposalId);
    setError('');
    try {
      await authApi.patch(`/schedule-proposals/${proposalId}/approve`);
      await Promise.all([loadScheduling(), fetchData()]);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Unable to approve schedule proposal.'));
    } finally {
      setActionId('');
    }
  };

  const rejectProposal = async (proposalId) => {
    setActionId(proposalId);
    setError('');
    try {
      await authApi.patch(`/schedule-proposals/${proposalId}/reject`, { reason: 'Rejected by supervisor during schedule review.' });
      await loadScheduling();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Unable to reject schedule proposal.'));
    } finally {
      setActionId('');
    }
  };

  const approveAll = async () => {
    setRunning(true);
    setError('');
    try {
      const batchId = grouped.proposed[0]?.batchId;
      await authApi.post('/schedule-proposals/approve-all', batchId ? { batchId } : {});
      await Promise.all([loadScheduling(), fetchData()]);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Unable to approve all proposals.'));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream" data-testid="supervisor-scheduling-page">
      <div className="border-b border-gray-100 bg-white">
        <div className="mx-auto flex max-w-7xl flex-col gap-5 px-6 py-8 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="caption text-burgundy-700">WAT scheduling</p>
            <h1 className="font-serif text-2xl font-bold text-gray-900">Visit Scheduling</h1>
            <p className="mt-1 text-sm text-gray-500">
              Next week runs {watDate(summary?.weekStart, { year: 'numeric' })} to {watDate(summary?.weekEnd, { year: 'numeric' })}, fixed WAT (UTC+01:00).
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={runGenerator}
              disabled={running}
              className="hotjar-btn hotjar-btn-primary bg-burgundy-700 hover:bg-burgundy-900"
              data-testid="generate-schedule-proposals"
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${running ? 'animate-spin' : ''}`} />
              {running ? (jobStatus || 'Scheduling') : 'Schedule Visits'}
            </button>
            <button
              onClick={approveAll}
              disabled={running || grouped.proposed.length === 0}
              className="hotjar-btn hotjar-btn-secondary"
              data-testid="approve-all-schedules"
            >
              <CheckCircle2 className="mr-2 h-4 w-4" />
              Approve All
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-6 py-8">
        {error && (
          <div className="mb-5 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {error}
          </div>
        )}

        {lastRun && (
          <div className="mb-5 rounded-xl border border-burgundy-100 bg-white p-4 text-sm text-gray-600">
            <span className="font-medium text-gray-900">Schedule run complete:</span>{' '}
            {lastRun.created || 0} proposed, {lastRun.unscheduled || 0} unscheduled for the next WAT week.
            Approved visits only appear after a supervisor approves proposed schedules.
          </div>
        )}

        <div className="mb-8 grid grid-cols-2 gap-5 lg:grid-cols-4">
          <StatCard label="Proposed" value={grouped.proposed.length} tone="yellow" />
          <StatCard label="Approved" value={grouped.approved.length} tone="green" />
          <StatCard label="Unscheduled" value={grouped.unscheduled.length} tone="red" />
          <StatCard label="Scheduled Visits" value={visits.filter(visit => visit.status === 'Scheduled').length} />
        </div>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
          <section className="xl:col-span-2">
            <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white">
              <div className="border-b border-gray-100 px-5 py-4">
                <h2 className="font-serif text-lg font-semibold text-gray-900">Proposed Schedule</h2>
                <p className="text-sm text-gray-500">Supervisor approval creates real scheduled visits.</p>
              </div>

              {loading ? (
                <div className="p-10 text-center text-gray-500">Loading schedules...</div>
              ) : proposals.length === 0 ? (
                <div className="p-10 text-center">
                  <Calendar className="mx-auto mb-3 h-12 w-12 text-gray-300" />
                  <h3 className="font-semibold text-gray-900">No proposals yet</h3>
                  <p className="text-sm text-gray-500">Generate next week’s WAT schedule to begin.</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {proposals.map((proposal) => (
                    <div key={proposal.id} className="p-5">
                      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                        <div className="min-w-0 flex-1">
                          <div className="mb-2 flex flex-wrap items-center gap-2">
                            <span className={`rounded-full border px-2.5 py-1 text-xs font-medium ${statusStyles[proposal.status] || statusStyles.Proposed}`}>
                              {proposal.status}
                            </span>
                            <span className="rounded-full bg-burgundy-50 px-2.5 py-1 text-xs font-medium text-burgundy-700">
                              {proposal.package}
                            </span>
                            <span className="text-xs text-gray-500">{proposal.requiredWeeklyVisits} visit(s)/week</span>
                          </div>
                          <h3 className="font-semibold text-gray-900">{proposal.dependantName}</h3>
                          <div className="mt-2 flex flex-wrap gap-4 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-4 w-4" />
                              {watDate(proposal.scheduledStart || proposal.scheduledDate)}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="h-4 w-4" />
                              {proposal.scheduledTimeLabel || 'WAT'}
                            </span>
                            {proposal.careOfficerName && (
                              <span className="flex items-center gap-1">
                                <Users className="h-4 w-4" />
                                {proposal.careOfficerName}
                              </span>
                            )}
                          </div>
                          {proposal.matchReasons?.length > 0 && (
                            <div className="mt-3 flex flex-wrap gap-2">
                              {proposal.matchReasons.slice(0, 4).map(reason => (
                                <span key={reason} className="rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-600">{reason}</span>
                              ))}
                            </div>
                          )}
                          {proposal.missingReason && (
                            <p className="mt-3 flex items-start gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700">
                              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                              {proposal.missingReason}
                            </p>
                          )}
                        </div>
                        {proposal.status === 'Proposed' && (
                          <div className="flex flex-shrink-0 gap-2">
                            <button
                              onClick={() => approveProposal(proposal.id)}
                              disabled={actionId === proposal.id}
                              className="rounded-full bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700"
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => rejectProposal(proposal.id)}
                              disabled={actionId === proposal.id}
                              className="rounded-full border border-red-200 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
                            >
                              Reject
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>

          <aside className="space-y-6">
            <div className="rounded-2xl border border-gray-100 bg-white">
              <div className="border-b border-gray-100 px-5 py-4">
                <h2 className="font-serif text-lg font-semibold text-gray-900">Scheduled Visits</h2>
                <p className="text-sm text-gray-500">Approved visits for the same WAT week.</p>
              </div>
              <div className="max-h-[560px] divide-y divide-gray-100 overflow-y-auto">
                {visits.length === 0 ? (
                  <p className="p-5 text-sm text-gray-500">
                    No approved visits scheduled for next week yet. Approve proposed schedules to create visits; unscheduled items need a matching care officer or an adjusted dependant preference.
                  </p>
                ) : visits.map(visit => (
                  <div key={visit.id} className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-medium text-gray-900">{visit.dependantName}</p>
                        <p className="mt-1 text-sm text-gray-500">{visit.careOfficerName}</p>
                        <p className="mt-2 flex items-center gap-1 text-xs text-gray-500">
                          <Clock className="h-3 w-3" />
                          {watDate(visit.scheduledStart || visit.scheduledDate)} · {new Date(visit.scheduledStart || visit.scheduledDate).toLocaleTimeString('en-NG', { timeZone: 'Africa/Lagos', hour: '2-digit', minute: '2-digit', hour12: false })} WAT
                        </p>
                      </div>
                      <span className="rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-700">{visit.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-burgundy-100 bg-white p-5">
              <h3 className="font-serif text-lg font-semibold text-gray-900">Scheduler Rules</h3>
              <div className="mt-4 space-y-3 text-sm text-gray-600">
                <p className="flex gap-2"><MapPin className="mt-0.5 h-4 w-4 text-burgundy-600" /> Location, gender preference, officer days, shift, and capacity are hard constraints.</p>
                <p className="flex gap-2"><Users className="mt-0.5 h-4 w-4 text-burgundy-600" /> Load is spread by weekly capacity ratio and same-day visit count.</p>
                <p className="flex gap-2"><Clock className="mt-0.5 h-4 w-4 text-burgundy-600" /> Times are fixed WAT (UTC+01:00), using 90-minute visit blocks.</p>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default SchedulingPage;
